// Licence and CACES entries of a temp worker (tempworkers.licences[]), as the
// admin validation screen reads them. `validation` is the api enum:
// 0 = to check, 1 = accepted, 2 = refused.

export const LICENCE_VALIDATION = {
  PENDING: 0,
  ACCEPTED: 1,
  REFUSED: 2,
};

// Badge types of components/badge/badgeSquare.
const STATUS_BADGES = {
  [LICENCE_VALIDATION.PENDING]: { type: 4, key: 'pending' },
  [LICENCE_VALIDATION.ACCEPTED]: { type: 5, key: 'accepted' },
  [LICENCE_VALIDATION.REFUSED]: { type: 3, key: 'refused' },
};

export const fillTemplate = (template, values) =>
  Object.entries(values).reduce(
    (text, [key, value]) => text.split(`{${key}}`).join(String(value)),
    String(template ?? ''),
  );

const readText = (language, key, fallback) =>
  language?.getValue ? language.getValue(key, fallback) : fallback;

/** « CACES R489 · catégorie 3 », or the catalogue name alone. */
export const getLicenceLabel = (entry, language) => {
  const name = (entry?.licence || '').toString().trim();
  const category = (entry?.category || '').toString().trim();
  if (!category) return name;
  const categoryText = fillTemplate(
    readText(
      language,
      'licenceValidation.categoryLabel',
      'catégorie {category}',
    ),
    { category },
  );
  return `${name} · ${categoryText}`;
};

/** { type, label } of the state badge of an entry. */
export const getLicenceStatusBadge = (validation, language) => {
  const badge =
    STATUS_BADGES[Number(validation)] ||
    STATUS_BADGES[LICENCE_VALIDATION.PENDING];
  return {
    type: badge.type,
    label: readText(language, `licenceValidation.status.${badge.key}`, ''),
  };
};

export const isLicencePending = (entry) =>
  Number(entry?.validation) === LICENCE_VALIDATION.PENDING;

/**
 * Same reading as the identity cards: entries already decided over all the
 * entries of the temp worker.
 */
export const countLicenceProgress = (licences = []) => ({
  handled: licences.filter((entry) => !isLicencePending(entry)).length,
  total: licences.length,
});

/**
 * JJ/MM/AAAA of a timestamp in the local time zone, or '' for a missing or
 * unreadable date. Not for calendar days stored at midnight UTC (expiration,
 * birthday): use formatCalendarDate, or the West Indies see the day before.
 */
export const formatDay = (value) => {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  return date.toLocaleDateString('fr-FR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  });
};

const isSameTitle = (a, b) =>
  (a?.licence || '') === (b?.licence || '') &&
  (a?.category || null) === (b?.category || null);

/**
 * The licences of a temp worker once a decision is known: the decided entry
 * takes its new state, and on acceptance the former accepted entry of the same
 * title leaves (the api moves it to the history in the same write).
 */
export const applyLicenceDecision = (licences = [], decision = {}) => {
  const { licenceId, action, licence: decidedEntry } = decision;
  const target = licences.find(
    (entry) => String(entry?._id) === String(licenceId),
  );
  if (!target) return licences;

  const isAccept = action === 'accept';
  const updatedTarget = {
    ...target,
    ...(decidedEntry && typeof decidedEntry === 'object' ? decidedEntry : {}),
    validation: isAccept
      ? LICENCE_VALIDATION.ACCEPTED
      : LICENCE_VALIDATION.REFUSED,
  };

  return licences
    .filter(
      (entry) =>
        entry === target ||
        !isAccept ||
        Number(entry?.validation) !== LICENCE_VALIDATION.ACCEPTED ||
        !isSameTitle(entry, updatedTarget),
    )
    .map((entry) => (entry === target ? updatedTarget : entry));
};

/** A CACES is a catalogue name that starts with « CACES ». */
export const isCacesLicence = (licenceName) =>
  /^CACES/i.test((licenceName || '').toString().trim());

const LICENCE_OPTION_SEPARATOR = '::';

/** Stable key of a licence title (catalogue name + category). */
export const getLicenceOptionKey = (licence, category) =>
  `${(licence || '').toString()}${LICENCE_OPTION_SEPARATOR}${(category || '').toString()}`;

/**
 * The catalogue (GET licence: [{ name, categories? }]) as select options: one
 * option per CACES category, one option for any other licence.
 */
export const flattenLicenceCatalogue = (catalogue, language) =>
  (Array.isArray(catalogue) ? catalogue : []).flatMap((item) => {
    const licence = (item?.name || '').toString().trim();
    if (!licence) return [];
    const categories = Array.isArray(item?.categories)
      ? item.categories.filter(Boolean)
      : [];
    const titles = categories.length
      ? categories.map((category) => ({ licence, category: String(category) }))
      : [{ licence, category: null }];
    return titles.map((title) => ({
      ...title,
      key: getLicenceOptionKey(title.licence, title.category),
      label: getLicenceLabel(title, language),
    }));
  });
